import apiClient from '../api.js'
import BaseApiService from '../BaseApiService.js'
import DepartmentService from './DepartmentService.js'

/**
 * Project Service
 * Handles all project-related API operations
 */
class ProjectService extends BaseApiService {
  constructor() {
    super('projects')
  }

  /**
   * Get all projects with optional filters and pagination
   * @param {object} params - Query parameters (status, department, manager, search, sort_by, sort_direction, per_page)
   * @returns {Promise} Projects list response
   */
  async getProjects(params = {}) {
    return await this.get('', params)
  }

  /**
   * Hand a project to its project manager (null clears the assignment).
   * Works for any project, whether or not it came from a Master Plan.
   */
  async assignManager(projectId, projectManagerId) {
    return this.post({ project_manager_id: projectManagerId }, `${projectId}/assign-manager`)
  }

  /**
   * Get project statistics for the current user
   * @returns {Promise} Project statistics (total, active, completed, planning, rejected)
   */
  async getProjectStatistics() {
    return await this.get('statistics')
  }

  /**
   * Get a specific project by ID
   * @param {string|number} id - Project ID
   * @returns {Promise} Project details response
   */
  async getProject(id) {
    return await this.get(id)
  }

  async getProjectOverview(id) {
    return await this.get(`${id}/overview`)
  }

  /**
   * Get project baseline data for change requests
   * @param {string|number} id - Project ID
   * @returns {Promise} Project baseline data (budget, end_date, deliverables)
   */
  async getProjectBaselineData(id) {
    return await this.get(`${id}/baseline-data`)
  }

  /**
   * Create a new project
   * @param {object} projectData - Project data including stakeholders, KPIs
   * @returns {Promise} Created project response
   */
  async createProject(projectData) {
    return await this.post(projectData)
  }

  async generateCode(departmentId) {
    return await this.get('generate-code', departmentId ? { department_id: departmentId } : {})
  }

  /**
   * Update an existing project
   * @param {string|number} id - Project ID
   * @param {object} projectData - Updated project data
   * @returns {Promise} Updated project response
   */
  async updateProject(id, projectData) {
    return await this.put(id, projectData)
  }

  /**
   * Delete a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Delete response
   */
  async deleteProject(id) {
    return await this.delete(id)
  }

  /**
   * Get project statistics
   * @returns {Promise} Project statistics response
   */
  async getStatistics() {
    return await this.get('statistics')
  }

  /**
   * Submit a draft/rejected project for approval
   * @param {string|number} id - Project ID
   * @returns {Promise} Submit response
   */
  async submitProject(id) {
    return await this.post({}, `${id}/submit`)
  }

  /**
   * Approve a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Approval response
   */
  async approveProject(id) {
    return await this.post({}, `${id}/approve`)
  }

  /**
   * Reject a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Rejection response
   */
  async rejectProject(id) {
    return await this.post({}, `${id}/reject`)
  }

  /**
   * Start a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Start response
   */
  async startProject(id) {
    return await this.post({}, `${id}/start`)
  }

  /**
   * Complete a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Completion response
   */
  async completeProject(id) {
    return await this.post({}, `${id}/complete`)
  }

  /**
   * Cancel a project
   * @param {string|number} id - Project ID
   * @returns {Promise} Cancellation response
   */
  async cancelProject(id) {
    return await this.post({}, `${id}/cancel`)
  }

  /**
   * Approve Phase 1 (Project Definition)
   * @param {string|number} id - Project ID
   * @param {string} comments - Optional approval comments
   * @param {string} projectClassification - Optional project classification (set by PMO)
   * @returns {Promise} Approval response
   */
  async approvePhase1(id, comments = '', projectClassification = null) {
    const data = { comments }
    if (projectClassification) {
      data.project_classification = projectClassification
    }
    return await this.post(data, `${id}/approve-phase1`)
  }

  /**
   * Reject Phase 1 (Project Definition)
   * @param {string|number} id - Project ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} Rejection response
   */
  async rejectPhase1(id, reason) {
    return await this.post({ reason }, `${id}/reject-phase1`)
  }

  // ==========================================
  // Multi-Stage Approval Workflow
  // ==========================================

  /**
   * Approve by PMO
   * @param {string|number} id - Project ID
   * @param {string} comments - Optional approval comments
   * @param {string} projectClassification - Project classification set by PMO
   * @returns {Promise} Approval response
   */
  async approvePMO(id, comments = '', projectClassification = null) {
    const data = { comments }
    if (projectClassification) {
      data.project_classification = projectClassification
    }
    return await this.post(data, `${id}/approve-pmo`)
  }

  /**
   * Approve by Planning Department
   * @param {string|number} id - Project ID
   * @param {string} comments - Optional approval comments
   * @returns {Promise} Approval response
   */
  async approvePlanning(id, comments = '') {
    return await this.post({ comments }, `${id}/approve-planning`)
  }

  /**
   * Reject by Planning Department
   * @param {string|number} id - Project ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} Rejection response
   */
  async rejectPlanning(id, reason) {
    return await this.post({ reason }, `${id}/reject-planning`)
  }

  /**
   * Approve by Project Board
   * @param {string|number} id - Project ID
   * @param {string} comments - Optional approval comments
   * @returns {Promise} Approval response
   */
  async approveBoard(id, comments = '') {
    return await this.post({ comments }, `${id}/approve-board`)
  }

  /**
   * Reject by Project Board
   * @param {string|number} id - Project ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} Rejection response
   */
  async rejectBoard(id, reason) {
    return await this.post({ reason }, `${id}/reject-board`)
  }

  /**
   * Submit Phase 2 (Execution Planning) for approval
   * @param {string|number} id - Project ID
   * @returns {Promise} Submission response
   */
  async submitPhase2(id) {
    return await this.post({}, `${id}/submit-phase2`)
  }

  /**
   * Approve Phase 2 (Execution Planning)
   * @param {string|number} id - Project ID
   * @param {string} comments - Optional approval comments
   * @returns {Promise} Approval response
   */
  async approvePhase2(id, comments = '') {
    return await this.post({ comments }, `${id}/approve-phase2`)
  }

  /**
   * Reject Phase 2 (Execution Planning)
   * @param {string|number} id - Project ID
   * @param {string} reason - Rejection reason
   * @returns {Promise} Rejection response
   */
  async rejectPhase2(id, reason) {
    return await this.post({ reason }, `${id}/reject-phase2`)
  }

  /**
   * Format project data for API submission
   * @param {object} formData - Form data from Vue component
   * @returns {object} Formatted data for API
   */
  formatProjectData(formData, options = {}) {
    const { allowIncomplete = false } = options
    const { phase1, phase2 } = formData

    // Base project data
    const projectData = {
      name: phase1.name,
      department_id: phase1.department,
      manager: phase1.manager,
      start_date: this.formatDate(phase1.startDate),
      end_date: this.formatDate(phase1.endDate),
      strategic_objectives: phase1.strategicObjectives,
      contribution_description: phase1.contributionDescription,
      budget: parseFloat(phase1.totalBudget) || 0,
      currency: phase1.currency,
      qualitative_outcomes: phase1.qualitativeOutcomes
    }

    // Add stakeholders if present
    if (phase1.stakeholders && phase1.stakeholders.length > 0) {
      projectData.stakeholders = phase1.stakeholders.map(stakeholder => ({
        ...(stakeholder.id ? { id: stakeholder.id } : {}),
        name: stakeholder.name,
        role: stakeholder.role,
        type: stakeholder.type,
        influence: stakeholder.influence ?? stakeholder.power,
        interest: stakeholder.interest,
        engagement_strategy: stakeholder.engagementStrategy ?? stakeholder.engagement_strategy,
        expectations: stakeholder.expectations,
        communication_method: stakeholder.communicationMethod ?? stakeholder.communication,
        communication_frequency: stakeholder.communicationFrequency ?? stakeholder.frequency,
        email: stakeholder.email,
        phone: stakeholder.phone,
        comment: stakeholder.comment
      }))
    }

    // Add KPIs if present
    if (phase1.quantitativeKPIs && phase1.quantitativeKPIs.length > 0) {
      const normalizedKpis = phase1.quantitativeKPIs.map(kpi => {
        const normalizedTarget = typeof kpi.target === 'number'
          ? kpi.target
          : kpi.target !== null && kpi.target !== undefined && kpi.target !== ''
            ? parseFloat(kpi.target)
            : null

        return {
          name: kpi.name ?? '',
          target: Number.isFinite(normalizedTarget) ? normalizedTarget : null,
          unit: kpi.unit ?? '',
          description: kpi.description || ''
        }
      })

      projectData.kpis = allowIncomplete
        ? normalizedKpis
        : normalizedKpis.filter(kpi => kpi.name && kpi.unit && kpi.target !== null && kpi.target !== undefined)
    }

    return projectData
  }

  /**
   * Format date for API (YYYY-MM-DD)
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date string
   */
  normalizeDateValue(value) {
    if (!value) {
      return null
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null
      }
      return new Date(value.getFullYear(), value.getMonth(), value.getDate())
    }

    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) {
        return null
      }

      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch
        const parsed = new Date(Number(year), Number(month) - 1, Number(day))
        return Number.isNaN(parsed.getTime()) ? null : parsed
      }

      const parsed = new Date(trimmed)
      if (Number.isNaN(parsed.getTime())) {
        return null
      }

      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
    }

    return null
  }

  formatDate(date) {
    const parsed = this.normalizeDateValue(date)
    if (!parsed) {
      return null
    }

    const year = parsed.getFullYear()
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  /**
   * Validate project data before submission
   * @param {object} formData - Form data to validate
   * @returns {object} Validation result with isValid and errors
   */
  validateProjectData(formData) {
    const errors = {}
    const { phase1 } = formData

    // Required field validation
    if (!phase1.name?.trim()) {
      errors.name = 'Project name is required'
    }

    if (!phase1.department) {
      errors.department = 'Department is required'
    }

    if (!phase1.manager) {
      errors.manager = 'Project manager is required'
    }

    if (!phase1.startDate) {
      errors.startDate = 'Start date is required'
    }

    if (!phase1.endDate) {
      errors.endDate = 'End date is required'
    }

    if (!phase1.totalBudget || parseFloat(phase1.totalBudget) <= 0) {
      errors.totalBudget = 'Total budget must be greater than 0'
    }

    // Date validation
    if (phase1.startDate && phase1.endDate) {
      const startDate = new Date(phase1.startDate)
      const endDate = new Date(phase1.endDate)
      
      if (startDate >= endDate) {
        errors.endDate = 'End date must be after start date'
      }
    }

    // Stakeholder validation
    if (phase1.stakeholders && phase1.stakeholders.length > 0) {
      const stakeholderErrors = []
      phase1.stakeholders.forEach((stakeholder, index) => {
        const stakeholderError = {}
        if (!stakeholder.name?.trim()) {
          stakeholderError.name = 'Stakeholder name is required'
        }
        const name = stakeholder.name?.trim()
        const role = stakeholder.role?.trim()
        const engagementStrategy = stakeholder.engagementStrategy?.trim() || stakeholder.engagement_strategy?.trim()
        const expectations = stakeholder.expectations?.trim()
        const email = stakeholder.email?.trim()
        const phone = stakeholder.phone?.trim()

        if (!name) {
          stakeholderError.name = 'Stakeholder name is required'
        } else if (name.length < 2) {
          stakeholderError.name = 'Stakeholder name must be at least 2 characters'
        } else if (name.length > 255) {
          stakeholderError.name = 'Stakeholder name must not exceed 255 characters'
        }

        if (!role) {
          stakeholderError.role = 'Stakeholder role is required'
        } else if (role.length < 2) {
          stakeholderError.role = 'Stakeholder role must be at least 2 characters'
        } else if (role.length > 100) {
          stakeholderError.role = 'Stakeholder role must not exceed 100 characters'
        }

        if (!stakeholder.type) {
          stakeholderError.type = 'Stakeholder type is required'
        }

        if (!stakeholder.influence) {
          stakeholderError.influence = 'Stakeholder influence level is required'
        }

        if (!stakeholder.interest) {
          stakeholderError.interest = 'Stakeholder interest level is required'
        }

        if (!engagementStrategy) {
          stakeholderError.engagementStrategy = 'Stakeholder engagement strategy is required'
        }

        if (!expectations) {
          stakeholderError.expectations = 'Stakeholder expectations are required'
        }

        if (!stakeholder.communicationMethod && !stakeholder.communication_method) {
          stakeholderError.communicationMethod = 'Stakeholder communication method is required'
        }

        if (!stakeholder.communicationFrequency && !stakeholder.communication_frequency) {
          stakeholderError.communicationFrequency = 'Stakeholder communication frequency is required'
        }

        if (!email) {
          stakeholderError.email = 'Stakeholder email is required'
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(email)) {
            stakeholderError.email = 'Stakeholder email must be a valid email address'
          } else if (email.length > 255) {
            stakeholderError.email = 'Stakeholder email must not exceed 255 characters'
          }
        }

        if (!phone) {
          stakeholderError.phone = 'Stakeholder phone number is required'
        } else {
          const digits = phone.replace(/[^0-9]/g, '')
          if (digits.length !== 10 || !digits.startsWith('05')) {
            stakeholderError.phone = 'Stakeholder phone must be 10 digits and start with 05'
          }
        }
      })
      
      if (stakeholderErrors.length > 0) {
        errors.stakeholders = stakeholderErrors
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  /**
   * Get project status options
   * @returns {Array} Status options for UI
   */
  getStatusOptions() {
    return [
      { label: 'Draft', value: 'draft' },
      { label: 'Pending', value: 'pending' },
      { label: 'Approved', value: 'approved' },
      { label: 'Rejected', value: 'rejected' },
      { label: 'Active', value: 'active' },
      { label: 'Completed', value: 'completed' },
      { label: 'Cancelled', value: 'cancelled' }
    ]
  }

  /**
   * Get currency options
   * @returns {Array} Currency options for UI
   */
  getCurrencyOptions() {
    return [
      { label: 'Saudi Riyal (SAR)', value: 'SAR' },
      { label: 'US Dollar (USD)', value: 'USD' },
      { label: 'Euro (EUR)', value: 'EUR' }
    ]
  }

  /**
   * Get department options for project creation
   * @returns {Promise<Array>} Department options for UI
   */
  async getDepartmentOptions() {
    try {
      const response = await DepartmentService.getDepartmentsDropdown()
      return response.data || []
    } catch (error) {
      console.error('Failed to fetch departments:', error)
      // Fallback to empty array
      return []
    }
  }

  /**
   * Update deliverable progress
   * @param {string|number} projectId - Project ID
   * @param {string|number} deliverableId - Deliverable ID
   * @param {object} data - Update data (update_target_value, latest_update, next_steps)
   * @returns {Promise} Update response
   */
  async updateDeliverable(projectId, deliverableId, data) {
    return await this.patch(`${projectId}/deliverables/${deliverableId}`, data)
  }

  // ==========================================
  // Project Deliverables (new table-based API)
  // ==========================================

  /**
   * Get all deliverables for a project
   * @param {string|number} projectId - Project ID
   * @returns {Promise} List of deliverables
   */
  async getProjectDeliverables(projectId) {
    return await this.get(`${projectId}/project-deliverables`)
  }

  /**
   * Create a new deliverable for a project
   * @param {string|number} projectId - Project ID
   * @param {object} data - Deliverable data
   * @returns {Promise} Created deliverable
   */
  async createProjectDeliverable(projectId, data) {
    return await this.post(data, `${projectId}/project-deliverables`)
  }

  /**
   * Update a project deliverable
   * @param {string|number} deliverableId - Deliverable ID
   * @param {object} data - Update data
   * @returns {Promise} Updated deliverable
   */
  async updateProjectDeliverable(deliverableId, data) {
    return await this.put(`project-deliverables/${deliverableId}`, data)
  }

  /**
   * Delete a project deliverable
   * @param {string|number} deliverableId - Deliverable ID
   * @returns {Promise} Delete response
   */
  async deleteProjectDeliverable(deliverableId) {
    return await this.delete(`project-deliverables/${deliverableId}`)
  }

  // ── Schedule Approval Workflow ───────────────────────────────────────────────

  /**
   * Switch the plan between duration-based and milestone-weight-based planning.
   * Only the formula changes; no task is rewritten.
   */
  async setScheduleMode(projectId, scheduleMode) {
    const { data } = await apiClient.patch(`projects/${projectId}/schedule/mode`, {
      schedule_mode: scheduleMode,
    })
    return data
  }

  /** Take a plan in from an MS Project XML or a Primavera XER file. */
  async importSchedule(projectId, file) {
    const form = new FormData()
    form.append('file', file)
    const { data } = await apiClient.post(`projects/${projectId}/schedule/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  /** The plan as a file the other tool can open, with the filename the server chose. */
  async exportSchedule(projectId, format) {
    const response = await apiClient.get(`projects/${projectId}/schedule/export`, {
      params: { format },
      responseType: 'blob',
    })
    const disposition = response.headers?.['content-disposition'] || ''
    const match = disposition.match(/filename="?([^"]+)"?/)
    return {
      data: response.data,
      filename: match?.[1] || `schedule.${format === 'primavera' ? 'xer' : 'xml'}`,
    }
  }

  async getScheduleApprovalWorkflow(projectId) {
    return await this.get(`${projectId}/schedule/approval-workflow`)
  }

  /**
   * Submit project schedule for PMO approval
   * @param {string|number} projectId
   */
  async submitSchedule(projectId) {
    return await this.post({}, `${projectId}/schedule/submit`)
  }

  /**
   * Withdraw schedule submission — revert pending → draft
   * @param {string|number} projectId
   */
  async withdrawSchedule(projectId) {
    return await this.post({}, `${projectId}/schedule/withdraw`)
  }

  /**
   * Approve project schedule (PMO / Admin)
   * @param {string|number} projectId
   */
  async approveSchedule(projectId, comment = '') {
    return await this.post({ comment }, `${projectId}/schedule/approve`)
  }

  /**
   * Reject project schedule (PMO / Admin)
   * @param {string|number} projectId
   * @param {string} reason - Rejection reason
   */
  async rejectSchedule(projectId, reason) {
    return await this.post({ reason }, `${projectId}/schedule/reject`)
  }
}

// Export singleton instance
export default new ProjectService()
